import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { unzip } from "@repo/utils";
import { CommentRootModel } from "@repo/structure";
import { getPdfPrinterVersionQuery } from "@query";
import {
  PDF_PRINT_PRESET,
  PDF_PRINT_VISIBILITY,
  PDF_SHOW_PRICES,
  PDF_USE_BORDERS,
  PROJECT_TYPE,
  SHARE_SETTINGS
} from "@utils";
import {
  EstimateCommentsProvider,
  EstimateEditorStore,
  EstimateEditorStoreProvider,
  EstimatePresetStore,
  EstimatePresetStoreProvider,
  UnsupportedBrowserAlert
} from "@tools";
import { PageCircularProgress } from "@components";
import { PrinterView } from "./PrinterView";
import "@styles/printer.css";
import { useSharedProjectParams } from "@hooks";

export const Printer = () => {
  const { company, project, projectHash, versionKey } = useSharedProjectParams();
  const [ searchParams ] = useSearchParams();
  const commentStore = useMemo(() => CommentRootModel.create({ groups: [] }), []);

  const [ store, setStore ] = useState(null);
  const [ presetStore, setPresetStore ] = useState(null);
  const [ unsupportedBrowserAlert, setUnsupportedBrowserAlert ] = useState(false);
  const [ useBorders ] = useState(searchParams.get(PDF_USE_BORDERS));

  useEffect(() => {
    onLoad();
  }, []);

  const onLoad = async () => {
    try {
      if (typeof Promise.withResolvers !== "function") {
        setUnsupportedBrowserAlert(true);
        return;
      }
      
      const presetId = searchParams.get(PDF_PRINT_PRESET);
      const applyVisibility = searchParams.get(PDF_PRINT_VISIBILITY);
      const printerPricesVisible = searchParams.get(PDF_SHOW_PRICES);
      let shareLinkSettings;
      
      try {
        shareLinkSettings = unzip(searchParams.get(SHARE_SETTINGS))
      } catch (e) {
        shareLinkSettings = null;
      }
      
      const {
        font,
        params,
        versionsName,
        structure: fetchedStructure,
        ...projectData
      } = await getPdfPrinterVersionQuery(company, project, projectHash, versionKey, presetId);

      let structure = JSON.parse(unzip(fetchedStructure));
      
      structure.hideEmptySectionDescriptions = true;
      if(applyVisibility) {
        if(structure.visibility)
          structure.visibility.apply = true;
        else
          structure.visibility = { apply: true };
      }
      
      if(presetId && params)
        setPresetStore(new EstimatePresetStore({ id: presetId, name: "", params, font }, undefined, true));
      else
        setPresetStore(new EstimatePresetStore());
      
      setStore(new EstimateEditorStore({
        estimateData: {
          ...projectData,
          versions: [ {
            key: versionKey,
            name: versionsName,
            structure,
            visible: 1
          }],
          shareLinkSettings,
        },
        estimateType: PROJECT_TYPE.STANDARD,
        unzipped: true,
        isSharedProposal: true,
        isPdfPrinter: true,
        printerPricesVisible,
      }));
    } catch (e) {
      console.error(e);
    }
  }

  if (unsupportedBrowserAlert)
    return <UnsupportedBrowserAlert variant="proposal" />;

  return !store ? (
    <PageCircularProgress />
  ) : (
    <EstimateCommentsProvider value={commentStore}>
      <EstimateEditorStoreProvider value={store}>
        <EstimatePresetStoreProvider value={presetStore}>
          <PrinterView useBorders={useBorders} />
        </EstimatePresetStoreProvider>
      </EstimateEditorStoreProvider>
    </EstimateCommentsProvider>
  );
};
